import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const STAGE_NAMES = { new: 'Ny', contacted: 'Kontaktad', demo: 'Demo', quote_sent: 'Offert', negotiation: 'Förhandling', won: 'Vunnen', lost: 'Förlorad', paused: 'Pausad' };
const PRICE_FIELDS = [['license_per_sensor_month', 'VapeCloud-licens (kr/sensor/mån)'], ['cage_price', 'Skyddsbur (kr/st)'], ['installation_price', 'Installation på plats (kr, exkl. resa)'], ['configuration_price', 'Konfiguration (kr)']];

export function CrmSettingsDialog({ open, onClose, settings, token, onSaved }) {
  const [s, setS] = useState(null);
  useEffect(() => { if (open && settings) setS(JSON.parse(JSON.stringify(settings))); }, [open, settings]);
  if (!s) return null;
  const p = s.pricing;
  const setP = (patch) => setS({ ...s, pricing: { ...p, ...patch } });
  const setTier = (i, k, v) => setP({ sensor_tiers: p.sensor_tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) });
  const save = async () => {
    const body = {
      pricing: { ...p, sensor_tiers: p.sensor_tiers.map(t => ({ min: +t.min, max: +t.max, price: +t.price })),
        ...Object.fromEntries(PRICE_FIELDS.map(([k]) => [k, +p[k]])) },
      probabilities: Object.fromEntries(Object.entries(s.probabilities).map(([k, v]) => [k, +v])),
    };
    const r = await fetch(`${API}/api/sales/crm/settings`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { toast.success('Prislista och sannolikheter sparade'); onSaved(); onClose(); }
    else toast.error(typeof d.detail === 'string' ? d.detail : 'Kontrollera värdena');
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="crm-settings-dialog">
        <DialogHeader><DialogTitle>Prislista och sannolikheter</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Gäller beräknade affärsvärden för leads. Befintliga offerter och individuella priser ändras inte. Alla priser exkl. moms.</p>
        <fieldset className="border rounded-lg p-3 space-y-2">
          <legend className="text-xs font-semibold px-1">Airpoint WiFi-sensorer (kr/st)</legend>
          <div className="grid grid-cols-8 gap-1.5 text-[11px] text-muted-foreground"><span className="col-span-2">Från (st)</span><span className="col-span-2">Till (st)</span><span className="col-span-3">Pris (kr/st)</span></div>
          {p.sensor_tiers.map((t, i) => (
            <div key={i} className="grid grid-cols-8 gap-1.5 items-center text-xs" data-testid={`tier-${i}`}>
              <Input className="col-span-2 h-8" type="number" value={t.min} onChange={e => setTier(i, 'min', e.target.value)} />
              <Input className="col-span-2 h-8" type="number" value={t.max} onChange={e => setTier(i, 'max', e.target.value)} />
              <Input className="col-span-3 h-8" type="number" value={t.price} onChange={e => setTier(i, 'price', e.target.value)} data-testid={`tier-price-${i}`} />
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setP({ sensor_tiers: p.sensor_tiers.filter((_, j) => j !== i) })}><Trash2 className="h-3 w-3 text-red-400" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="text-xs" onClick={() => setP({ sensor_tiers: [...p.sensor_tiers, { min: 1, max: 1, price: 0 }] })}><Plus className="h-3 w-3 mr-1" /> Lägg till nivå</Button>
        </fieldset>
        <div className="grid grid-cols-2 gap-2">
          {PRICE_FIELDS.map(([k, l]) => (
            <div key={k}><label className="text-[11px] text-muted-foreground">{l}</label><Input type="number" min="0" value={p[k]} onChange={e => setP({ [k]: e.target.value })} data-testid={`price-${k}`} /></div>
          ))}
        </div>
        <fieldset className="border rounded-lg p-3">
          <legend className="text-xs font-semibold px-1">Standardsannolikhet per steg (%)</legend>
          <p className="text-[11px] text-muted-foreground mb-2">Uppskattningar – inte uppmätta konverteringsgrader.</p>
          <div className="grid grid-cols-4 gap-2">
            {Object.keys(STAGE_NAMES).map(k => (
              <div key={k}><label className="text-[11px] text-muted-foreground">{STAGE_NAMES[k]}</label>
                <Input type="number" min="0" max="100" value={s.probabilities[k] ?? 0} onChange={e => setS({ ...s, probabilities: { ...s.probabilities, [k]: e.target.value } })} data-testid={`prob-${k}`} /></div>
            ))}
          </div>
        </fieldset>
        <DialogFooter><Button variant="outline" onClick={onClose}>Avbryt</Button><Button onClick={save} data-testid="crm-settings-save">Spara</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
